import React, { Fragment, useMemo, useState } from 'react';
import AppTable from '../../../../constants/AppTable';
import { LoadingSpinner } from '../../../../constants/AppLoading';
import DialogBox from '../../../../constants/DialogBox';
import { Grid } from '@mui/material';
import InputTextBox from '../../../../constants/InputTextBox';
import { readerEditValidationSchema, readerValidationSchema, toleValidationSchema } from '../../../../helpers/validation';
import { useFormik } from 'formik';
import NepaliInputBox from '../../../../constants/NepaliInputBox';
import { handleNepaliInputChange } from '../../../../helpers/handleChange';
import { EndBaseLine } from '../../../../constants/AppSpace';
import { Button } from '../../../../constants/Button';
import { putDataToTheServer } from '../../../../helpers/api';
import AppURL from '../../../../constants/AppURL';
import { ToastSuccess, ToastWarning } from '../../../../constants/Toast';
import { ItemCategory, ProductMaster, Reader } from '../../../../helpers/models/Models';
import DefaultSelectInputBox from '../../../../constants/DefaultSelectBox';
import { MasterCategoryOptions, YesNoOptions } from '../../../../constants/Options';

interface ItemCategoryListViewProps {
    results: {
        results: ItemCategory[];
        previous: string | null;
        next: string | null;
    } | null;
    loading: boolean;
    error: string | null;
    setOnURL: React.Dispatch<React.SetStateAction<string>>;
    handleFormSuccess: () => void;
    data: {
        results: ProductMaster[]
    }|null;
}

const ItemCategoryListView: React.FC<ItemCategoryListViewProps> = ({ data, results, loading, error, handleFormSuccess, setOnURL }) => {
    const masterCategoryOptions = MasterCategoryOptions(data?.results)

    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState<ItemCategory | null>(null);

    const columns = [
        { header: 'SN', accessor: 'sn', sortable: false },
        { header: 'Master Category Name', accessor: 'product_master_name', sortable: true },
        { header: 'Item Category', accessor: 'name', sortable: false },
        { header: 'Master Cate. Name(Nepali)', accessor: 'name_nep', sortable: false },
        { header: 'Actions', accessor: 'actions', action: true },
    ];
    const modifiedData = useMemo(() => {
        if (!results) return [];
        return results.results.map((item: ItemCategory) => ({
            sn: item.sn,
            product_master: item.product_master,
            name: item.name,
            name_nep: item.name_nep,
            active: item.status,
            status: item.status,
            product_master_name: item.product_master_name
        }));
    }, [results]);

    const handleEdit = (item: ItemCategory) => {
        setCurrentItem(item);
        setDialogOpen(true);
    };

    const formik = useFormik({
        initialValues: {
            product_master: currentItem?.product_master || "",
            name: currentItem?.name || "",
            name_nep: currentItem?.name_nep || "",
            status: currentItem?.status === 1 ? "1" : "2" || "",
        },
        // validationSchema: readerEditValidationSchema,
        enableReinitialize: true,
        onSubmit: async (values, action) => {
            const formData = new FormData();
            formData.append("name", values.name);
            formData.append("name_nep", values.name_nep);
            formData.append("status", values.status === "2" ? "0" : "1");
            formData.append("product_master", values.product_master?.toString());
            try {
                await putDataToTheServer(AppURL.InventoryURL + 'inventory/product/sub/category/retrieve/update/view/' + currentItem?.sn, formData).then((res) => {
                    if (res?.status === 200) {
                        handleFormSuccess();
                        setDialogOpen(false);
                        ToastSuccess({ message: res?.message });
                    } else if (res?.status === 400) {
                        ToastWarning({ message: res?.message });
                    }
                    else {
                        ToastWarning({ message: "Some Issue" });
                    }
                });
            } catch (error) {
                console.log(error);
            }
        },
    });

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <LoadingSpinner />
            </div>
        );
    }

    if (error) {
        return <p>Error loading data: {error}</p>;
    }

    return (
        <Fragment>
            <AppTable
                columns={columns}
                data={modifiedData}
                onEdit={handleEdit}
                onNext={() => results?.next && setOnURL(results.next)}
                onPrev={() => results?.previous && setOnURL(results.previous)}
            />
            {dialogOpen && currentItem && (
                <DialogBox open={dialogOpen} headerName='Master Category Edit Update' close={() => setDialogOpen(false)}>
                    <form onSubmit={formik.handleSubmit}>
                        <Grid container columnSpacing={3} rowSpacing={0.8}>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <DefaultSelectInputBox
                                    name="product_master"
                                    label='Product Master'
                                    disabled={false} // Adjust based on your needs
                                    options={masterCategoryOptions}
                                    value={formik.values.product_master}
                                    optionSelect={(name, selectedOption) => {
                                        formik.setFieldValue(name, selectedOption.value);
                                    }}
                                    // errors={error.product_master}
                                    placeholder="Select Product Master"
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='name'
                                    label='Master Category Name'
                                    handleChange={formik.handleChange}
                                    value={formik.values.name}
                                    error={typeof formik.errors.name === 'string' ? formik.errors.name : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.name}
                                    required={true}
                                    placeholder='Enter Master Category Name'
                                />
                            </Grid>

                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <NepaliInputBox
                                    label='Master Category Name(Nepali)'
                                    name='name_nep'
                                    value={formik.values.name_nep}
                                    handleChange={(e) => handleNepaliInputChange(e, { setFieldValue: formik.setFieldValue, name: 'name_nep' })}
                                    required={true}
                                    handleBlur={formik.handleBlur}
                                    error={typeof formik.errors.name_nep === 'string' ? formik.errors.name_nep : undefined}
                                    placeholder='Enter Master Category(Nepali)'
                                    touched={formik.touched.name_nep}
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <DefaultSelectInputBox
                                    name="status"
                                    label='Active'
                                    disabled={false} // Adjust based on your needs
                                    options={YesNoOptions}
                                    value={formik.values.status}
                                    optionSelect={(name, selectedOption) => {
                                        formik.setFieldValue(name, selectedOption.value);
                                    }}
                                    clear={true}
                                    placeholder="Select Options"
                                />
                            </Grid>

                            <Grid item xl={12} lg={12} sm={12} md={12} xs={12}>
                                <EndBaseLine top={18}>
                                    <Button btn_type='update' type='submit' el='button' />
                                </EndBaseLine>
                            </Grid>
                        </Grid>
                    </form>
                </DialogBox>
            )}
        </Fragment>
    );
};

export default ItemCategoryListView;
